import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaMapMarkerAlt } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { formatTimeSlots, diffMinutes, formatMinutesAsHM } from "../../../util_functions/helpers";
import { useJobCreate } from "../../../context/JobCreateContext";

const JobReviewAndPostComponent = ({
  setCurrentStep,
  user,
  jobPostedSuccess,
}) => {
  const navigate = useNavigate();

  const { jobSelections, fetchJobSelectionsById, getJobCreateIdFromCookies } =
    useJobCreate();

  const successMsgRef = useRef(null);
  const headingRef = useRef(null);

  const [successMsgHeight, setSuccessMsgHeight] = useState(0);
  const [headingHeight, setHeadingHeight] = useState(0);

  const selectedLocations = Array.isArray(jobSelections?.selectedLocations)
    ? jobSelections.selectedLocations.filter(Boolean)
    : [];


  useEffect(() => {
    if (jobPostedSuccess) {
      const timer = setTimeout(() => {
        navigate("/account/fs-dashboard/my-jobs/open");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [jobPostedSuccess, navigate]);

  useEffect(() => {
    if (successMsgRef.current && jobPostedSuccess) {
      setSuccessMsgHeight(successMsgRef.current.offsetHeight);
    } else {
      setSuccessMsgHeight(0);
    }
    if (headingRef.current) {
      setHeadingHeight(headingRef.current.offsetHeight);
    }
  }, [jobPostedSuccess]);

  useEffect(() => {
    const jobCreateId = getJobCreateIdFromCookies();
    if (!jobCreateId) {
      navigate("/account/fs-dashboard/my-jobs/new/standard");
    } else {
      fetchJobSelectionsById(jobCreateId).catch((error) => {
        console.error(
          "[JobReviewAndPostComponent] Failed to fetch job by ID:",
          error
        );
      });
    }
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById, navigate]);

  useEffect(() => {
    if (jobSelections) {
      console.log("Job Selections Review : ", jobSelections);
    }
  }, [jobSelections]);

  const handleEditSelectedLocation = () => {
    navigate("/account/fs-dashboard/my-jobs/new/standard");
  };

  const handleEditJobSupportTypeDetails = () => {
    setCurrentStep(1);
  };

  const handleEditCareActivitiesDetails = () => {
    setCurrentStep(2);
  };

  const handleEditCommunityDetails = () => {
    setCurrentStep(3);
  };

  const handleEditFrequencyDetails = () => {
    setCurrentStep(4);
  };

  const handleEditJobCraftDetails = () => {
    setCurrentStep(5);
  };

  const formatMoney = (n, currency = "USD") => {
    const num = Number(n);
    if (!Number.isFinite(num)) return "-";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `${num.toFixed(2)} ${currency}`;
    }
  };

  const unitSuffix = (payType) =>
    payType === "hourly" ? "/ hr" :
      payType === "daily" ? "/ day" :
        payType === "annual" ? "/ year" : "";

  const buildCompString = (fd) => {
    const pt = ["hourly", "daily", "annual"].includes(fd?.payType) ? fd.payType : "";
    if (!pt) return "-";

    const c = fd?.currency || "USD";
    const fixed =
      pt === "hourly" ? fd?.payRateHourly :
        pt === "daily" ? fd?.payRateDaily :
          fd?.payRateAnnual;

    if (fixed != null) {
      return `${formatMoney(fixed, c)} ${unitSuffix(pt)}`;
    }

    const rng = fd?.payRanges?.[pt] || {};
    const { min, max } = rng;

    if (min != null && max != null) {
      if (Number(min) === Number(max)) {
        return `${formatMoney(min, c)} ${unitSuffix(pt)}`;
      }
      return `${formatMoney(min, c)} – ${formatMoney(max, c)} ${unitSuffix(pt)}`;
    }
    if (min != null) return `≥ ${formatMoney(min, c)} ${unitSuffix(pt)}`;
    if (max != null) return `≤ ${formatMoney(max, c)} ${unitSuffix(pt)}`;
    return "-";
  };



  return (
    <div className="job-create-review-post-component">
      {jobPostedSuccess && (
        <div className="claim-success-message" ref={successMsgRef}>
          <FaCheckCircle className="invoice-check-icon" />
          <h3>
            Job Submitted Successfully. It will be published shortly after
            review.
          </h3>
        </div>
      )}

      <div
        className="job-create-review-container"
      >
        <div className="job-details-review">
          <h3>Job Details</h3>
          <button
            className="job-edit-button"
            onClick={handleEditJobCraftDetails}
            disabled={jobPostedSuccess}
          >
            <FiEdit2 className="job-edit-button__icon" aria-hidden="true" />
            <span>Edit</span>
          </button>
        </div>
        <div className="job-title-section">
          <div className="job-review-item">
            <h4>Position Title</h4>
            <p>{jobSelections.jobCraftDetails.jobTitle}</p>
          </div>

          <div className="job-review-item">
            <h4>Job Description</h4>
            <div
              className="dangerously-formatted"
              dangerouslySetInnerHTML={{
                __html: jobSelections.jobCraftDetails.jobDescription,
              }}
            ></div>
          </div>
          <div className="job-review-item">
            <h4>Preferred Qualities</h4>
            <div
              className="dangerously-formatted"
              dangerouslySetInnerHTML={{
                __html: jobSelections.jobCraftDetails.preferredQualities,
              }}
            ></div>
          </div>
          <div className="job-review-item">
            <h4>Other considerations</h4>
            <div
              className="dangerously-formatted"
              dangerouslySetInnerHTML={{
                __html: jobSelections.jobCraftDetails.personalDetails,
              }}
            ></div>
          </div>
        </div>

        <div className="job-details-review">
          <h3>Support Type(s)</h3>
          <button
            className="job-edit-button"
            onClick={handleEditJobSupportTypeDetails}
            disabled={jobPostedSuccess}
          >
            <FiEdit2 className="job-edit-button__icon" aria-hidden="true" />
            <span>Edit</span>
          </button>
        </div>

        <div className="job-title-section">
          <div className="support-type-list">
            <ul className="support-activity-list">
              {jobSelections.supportTypeDetails?.map((detail, i) => (
                <li className="selected-activity-item selected" key={i}>
                  <span className="support-activity-icon" aria-hidden="true">✓</span>
                  <span className="support-activity-text">{detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="job-details-review">
          <h3>Support activities</h3>
          <button
            className="job-edit-button"
            onClick={handleEditCareActivitiesDetails}
            disabled={jobPostedSuccess}
          >
            <FiEdit2 className="job-edit-button__icon" aria-hidden="true" />
            <span>Edit</span>
          </button>
        </div>

        <div className="job-title-section">
          <div className="support-type-list">
            <ul className="support-activity-list">
              {jobSelections.careActivityDetails?.map((detail, i) => (
                <li className="selected-activity-item selected" key={i}>
                  <span className="support-activity-icon" aria-hidden="true">✓</span>
                  <span className="support-activity-text">{detail}</span>
                </li>
              ))}
            </ul>


          </div>
        </div>

        <div className="job-details-review">
          <h3>Selected location(s)</h3>
          <button
            className="job-edit-button"
            onClick={handleEditSelectedLocation}
            disabled={jobPostedSuccess}
          >
            <FiEdit2 className="job-edit-button__icon" aria-hidden="true" />
            <span>Edit</span>
          </button>
        </div>

        <div className="job-title-section">
          {selectedLocations.length > 0 ? (
            <ul className="selected-locations-list">
              {selectedLocations.map((loc, idx) => (
                <li key={idx}>
                  <FaMapMarkerAlt className="chip-icon" aria-hidden="true" />
                  <span className="chip-text">{loc}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No locations selected.</p>
          )}
        </div>

        <div className="job-details-review">
          <h3>Activities workplace</h3>
          <button
            className="job-edit-button"
            onClick={handleEditCommunityDetails}
            disabled={jobPostedSuccess}
          >
            <FiEdit2 className="job-edit-button__icon" aria-hidden="true" />
            <span>Edit</span>
          </button>
        </div>

        <div className="job-title-section">
          <table className="review-table">
            <caption className="sr-only">Activities workplace</caption>
            <tbody>
              <tr>
                <th scope="row">Support place</th>
                <td>{jobSelections.supportJobLocationDetails?.supportJobLocation || "-"}</td>
              </tr>
              <tr>
                <th scope="row">Preferred Gender</th>
                <td>{jobSelections.supportJobLocationDetails?.preferredGender || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="job-details-review">
          <h3>Recurrence & Scheduling</h3>
          <button
            className="job-edit-button"
            onClick={handleEditJobCraftDetails}
            disabled={jobPostedSuccess}
          >
            <FiEdit2 className="job-edit-button__icon" aria-hidden="true" />
            <span>Edit</span>
          </button>
        </div>

        <div className="job-title-section">
          {(() => {
            const fd = jobSelections?.frequencyDetails || {};

            const shiftTypes = Array.isArray(fd.shiftTypes) ? fd.shiftTypes : [];

            const isOneOff = String(jobSelections?.frequencyDetails?.supportType || fd?.supportType || "")
              .toLowerCase() === "one-off";
            return (

              <table className="review-table">
                <caption className="sr-only">Frequency &amp; Scheduling</caption>
                <tbody>
                  <tr>
                    <th scope="row">Recurrence Type</th>
                    <td>
                      <div className="session-hourly-rate">
                        {fd.supportType ?? "-"}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <th scope="row">Job type</th>
                    <td>
                      <div className="session-hourly-rate">
                        {fd.jobType ?? "-"}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <th scope="row">Shift types</th>
                    <td>
                      {shiftTypes.length ? (
                        <ul className="shift-types-grid" role="list">
                          {shiftTypes.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      ) : (
                        "-"
                      )}
                    </td>

                  </tr>

                  <tr>
                    <th scope="row">Pay Rate ( <strong>{fd.payType ? fd.payType.charAt(0).toUpperCase() + fd.payType.slice(1) : "-"} </strong>)</th>
                    <td>
                      <div className="session-hourly-rate">
                        {buildCompString(fd)}
                      </div>
                    </td>
                  </tr>

                  <tr>
  <th scope="row">
    {(fd.sessionsPerWeek ?? 0) > 1 ? "Sessions" : "Session"}
  </th>
  <td>{fd.sessionsPerWeek ?? "-"}</td>
</tr>


{/* 
                  <tr>
                    <th scope="row">Hours per session</th>
                    <td>{fd.hoursPerSession ?? "-"}</td>
                  </tr> */}

                  <tr>
                    <th scope="row">Scheduling</th>
                    <td><div className="session-hourly-rate ">
                      {fd.scheduling || "-"}
                    </div>
                    </td>
                  </tr>

                  <tr>
                    <th scope="row">Scheduling notes</th>
                    <td>
                      {(() => {
                        const notes = (jobSelections?.frequencyDetails?.additionalDetails || "").trim();
                        return notes
                          ? <div style={{ whiteSpace: "pre-wrap" }}>{notes}</div>
                          : "-";
                      })()}
                    </td>
                  </tr>


                  <tr>
                    <th scope="row">Selected start date</th>
                    <td>{fd.supportStartDate || "-"}</td>
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div>

        {jobSelections?.frequencyDetails?.scheduling === "Preferred" && (() => {
          const fd = jobSelections.frequencyDetails || {};
          const preferredDays = fd.preferredDays || {};
          const timeSlots = fd.timeSlots || {};
          const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

          const rows = DAYS.filter(
            d => (preferredDays[d] && (timeSlots[d] || []).length > 0) || (timeSlots[d] || []).length > 0
          );

          return (
            <div className="job-title-section">
              <h5>Preferred Days &amp; Time Slots</h5>
              <table className="review-table">
                <tbody>
                  {rows.length > 0 ? rows.map((day) => {
                    const slots = (timeSlots[day] || []);
                    const totalMins = slots.reduce((sum, s) => sum + diffMinutes(s.from, s.to), 0);
                    const totalHM = formatMinutesAsHM(totalMins);

                    return (
                      <tr key={day}>
                        <th scope="row">{day}</th>
                        <td className="time-slots-cell">
                          {slots.length > 0 ? (
                            slots.map((s, i) => (
                              <span className="slot-chip" key={`${day}-${i}`}>
                                {s.from} – {s.to}
                              </span>
                            ))
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="hours-cell slot-chip">{slots.length ? totalHM : "-"}</td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={3}>No preferred days selected.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}

        <div className="job-details-review">
          <h3>Posted by</h3>
        </div>
        <div className="job-review-item">
          <p>
            {user?.firstName || "Unknown"} {user?.lastName?.charAt(0) || ""}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default JobReviewAndPostComponent;
